"use client";

import { useState, useEffect, useRef, Fragment  } from "react";
import LazyLoadImage from "./LazyLoadImage";
import Link from "next/link";
import Container from "@/components/elements/Container";
import { InternalCTALink } from "@/components/elements/WebLink";
import BirchLogo from "./BirchLogo";
import { jsonToHtml } from "@contentstack/json-rte-serializer";
import { useCart } from "@/contexts/CartContext";

// Arrow icon matching production SVG (rotates on group hover)
const ArrowIcon = () => (
  <svg
    className="ml-1 h-3 w-3 transition-none group-hover:rotate-180"
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    aria-hidden="true"
    viewBox="0 0 21 21"
  >
    <path d="M11 20.9h-.3A31 31 0 0 0 1 12.1l1.5-2c3.4 2.3 6 4.5 7.5 6.7-.1-1.4-.3-3-.3-5.2V.9H12v10.7c0 2.1 0 3.8-.2 5.2 1.5-2.2 4-4.4 7.4-6.6l1.6 1.9a31 31 0 0 0-9.9 8.8Z" />
  </svg>
);

// Cart icon matching production SVG exactly
const CartIcon = ({ count = 0 }) => (
  <div className="inline-flex items-start">
    <svg
      className="text-primary w-5 h-5"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
      aria-hidden="true"
      viewBox="0 0 1000 1000"
    >
      <path d="M742.8 417.7V246.3c0-64.6-25.3-125.5-71.3-171.5S564.6 3.5 500 3.5 374.5 28.8 328.5 74.8s-71.3 106.9-71.3 171.5v171.4H49.1l59.3 276.6C153.7 860.5 235 996.5 362.9 996.5h274.2c127.8 0 209.2-136 254.5-302.2l59.3-276.6ZM337.2 246.3a162.8 162.8 0 0 1 325.6 0v171.4H337.2Z" />
    </svg>

    {count > 0 && (
      <span className="font-worksans -ml-1 -mt-2 min-w-[19px] h-[19px] px-1 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center leading-none">
        {count}
      </span>
    )}
  </div>
);

const HamburgerIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <line
      x1="3"
      y1="6"
      x2="21"
      y2="6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="3"
      y1="12"
      x2="21"
      y2="12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="3"
      y1="18"
      x2="21"
      y2="18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="14"
    height="20"
    viewBox="4 4 14 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M18 6L6 18M6 6l12 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M9 18l6-6-6-6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BackArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15 18l-6-6 6-6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Dropdown column renderers ────────────────────────────────────────────────

const ProductCard = ({
  card,
  compact = false,
  onLinkClick,
  shouldLoadImages = true,
}) => {
  const descriptionHtml = card.description ? jsonToHtml(card.description) : "";
  const hasContent = descriptionHtml.replace(/<[^>]*>/g, "").trim().length > 0;

  // BL-513: the eyebrow, heading and description used to sit inside one link,
  // so its text ran to 161 characters — "Use with Foundation or Box SpringMetal
  // Bed FramePairs perfectly with...". An aria-label fixed what a screen reader
  // announces but not what Crownpeak measures, which is the raw text.
  //
  // The link now wraps the product name only, and covers the card with an
  // ::after overlay so the whole card stays clickable — the point of wrapping
  // it — while the link text stays short. The overlay needs the z-index: the
  // image sits in its own positioned box and would otherwise paint on top of it
  // and swallow the click.
  const linkText = card.heading || card.link?.title || card.preheading || 'View product';

  // BL-747 review: every desktop dropdown link rendered at 20px, where
  // production sizes them by what kind of card they are. Measured on the live
  // site: the Mattresses dropdown, whose entries are cards with a thumbnail,
  // runs at 14px; Richard read the Eco-Conscious column, which is plain text
  // links with no image, at 18px. So the split is the one this component
  // already makes on `card.image`, it just was not carried into the size.
  //
  // The "Shop All …" links are a different element and were already 18px, so
  // they are untouched. Mobile keeps text-base — `compact` is only true in the
  // drawer, and nothing in the review was about mobile.
  //
  // Bedding and More are extrapolated: the rule comes from measuring
  // Mattresses and Eco-Conscious on production, not those two.
  const cardSize = compact ? "text-base" : card.image ? "text-sm" : "text-lg";
  const headingClass = `${card.image ? "flex flex-col font-semibold" : "hover:underline"} leading-[150%] text-primary ${cardSize}`;

  return (
    <div
      className={`relative flex w-full ${card.image ? "min-h-24" : ""} has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-primary`}
    >
      {card.image && (
        <div
          className={`relative flex-shrink-0 ${
            compact ? "w-[90px] h-[90px]" : "w-[150px] h-[150px]"
          }`}
        >
          <LazyLoadImage
            shouldLoad={shouldLoadImages}
            className="object-cover"
            src={card.image.url}
            alt={card.image.description || card.image.title || ""}
            fill
            sizes={compact ? "90px" : "150px"}
          />
        </div>
      )}
      <div
        className={`${card.image ? "flex flex-col px-4 py-2" : ""} text-left font-sans normal-case leading-5`}
      >
        {card.preheading && (
          <div className="flex mb-1">
            <div
              className="text-xs font-semibold px-2 py-1 bg-white text-primary"
            >
              {card.preheading}
            </div>
          </div>
        )}
        <Link
          prefetch={false}
          href={card.link.href}
          onClick={onLinkClick}
          className={`no-underline focus-visible:outline-none after:absolute after:inset-0 after:z-10 after:content-[''] ${card.heading ? headingClass : ""}`}
        >
          {card.heading || <span className="sr-only">{linkText}</span>}
        </Link>
        {hasContent && (
          <div
            className="text-xs font-normal text-primary"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        )}
      </div>
    </div>
  );
};

const ProductCardsColumn = ({ col, compact = false, onLinkClick, shouldLoadImages = true }) => {
  return (
    <>
      {col?.section?.map((sectionItem, index) => {
        return (
          <Fragment key={index}>
            {sectionItem?.section_header && (
              <div className="text-left text-2xl text-primary font-bold">
                {sectionItem?.section_header}
              </div>
            )}
            {/* BL-746: production renders this image 242px tall at 485px wide,
                not 258. What matters is the ratio rather than either number —
                the wrapper is `w-full`, so the rendered width follows the
                dropdown column and is 400px at a 1440 viewport. */}
            {sectionItem?.section_header_image?.url && (
              <div className="relative w-full aspect-[485/242]">
                <LazyLoadImage
                  shouldLoad={shouldLoadImages}
                  src={sectionItem?.section_header_image?.url}
                  alt={
                    sectionItem?.section_header_image?.description ||
                    sectionItem?.section_header_image?.title || ""
                  }
                  className="object-cover"
                  fill
                  sizes="485px"
                />
              </div>
            )}
            {!sectionItem?.section_header_image?.url &&
              sectionItem?.section_items.map((card, index) => (
                <ProductCard
                  key={`${card.link?.href}-${index}`}
                  card={card}
                  compact={compact}
                  onLinkClick={onLinkClick}
                  shouldLoadImages={shouldLoadImages}
                />
              ))}
          </Fragment>
        );
      })}
      {/* BL-421/BL-847: production treats this CTA differently per breakpoint, and the
          two are separate elements there rather than one restyled by media
          query. Measured on production: the desktop panel renders a filled pill
          (white on #006ec8), the mobile drawer a blue text link sitting above a
          detached 2px rule that clears on hover. `compact` is already how this
          component knows which context it is in — the drawer passes it, the
          desktop panel does not. */}
      {col?.column_call_to_action?.column_link?.href && col?.column_call_to_action?.label && (
        compact ? (
          <div className="mt-2">
            <Link
              prefetch={false}
              href={col.column_call_to_action.column_link.href}
              onClick={onLinkClick}
              data-cy="cta-link"
              className="relative inline-flex px-2 py-3 text-lg text-sea no-underline after:absolute after:inset-x-2 after:bottom-1.5 after:h-0.5 after:bg-current after:content-[''] hover:after:h-0"
              aria-label={col.column_call_to_action.column_link.title}
            >
              {col.column_call_to_action.label}
            </Link>
          </div>
        ) : (
          <div onClick={onLinkClick}>
            <InternalCTALink
              label={col.column_call_to_action.label}
              link={col.column_call_to_action.column_link.href}
              showIcon={false}
              className="border-2 border-transparent hover:bg-transparent hover:text-sea hover:border-sea"
            />
          </div>
        )
      )}
    </>
  );
};

const LinkSectionsColumn = ({ col, onLinkClick }) => (
  <>
    {col.sections.map((section) => (
      <div key={section.heading} className="flex flex-col gap-4">
        <div className="text-left text-2xl text-primary">{section.heading}</div>
        {section.links.map((link) => (
          <Link
            prefetch={false}
            key={link.href}
            href={link.href}
            onClick={onLinkClick}
            className="normal-case no-underline flex gap-4 items-center text-primary font-normal"
          >
            <div
              className="text-lg font-normal capitalize hover:underline text-left"
              style={{ lineHeight: "150%" }}
            >
              {link.label}
            </div>
          </Link>
        ))}
      </div>
    ))}
  </>
);

const MerchCardColumn = ({ col, onLinkClick, shouldLoadImages = true }) => (
  <div className="relative h-full min-h-52">
    <Link
      prefetch={false}
      href={col.href}
      className="absolute inset-0 z-10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
      aria-label={col.label}
      onClick={onLinkClick}
    />

    <LazyLoadImage
      shouldLoad={shouldLoadImages}
      className="object-cover rounded-xl pointer-events-none"
      src={col.image}
      alt={col.label}
      fill
      sizes="485px"
    />
  </div>
);

const colsClass = { 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" };

const ColumnRenderer = ({ layout, col, onLinkClick, compact = false, shouldLoadImages = true }) => {
  if (layout === "product-cards") return <ProductCardsColumn col={col} onLinkClick={onLinkClick} compact={compact} shouldLoadImages={shouldLoadImages} />;
  if (layout === "link-sections") return <LinkSectionsColumn col={col} onLinkClick={onLinkClick} />;
  if (layout === "merch-card") return <MerchCardColumn col={col} onLinkClick={onLinkClick} shouldLoadImages={shouldLoadImages} />;
  return null;
};

// Dropdown is ALWAYS rendered in the DOM; visibility is toggled via CSS.
// Links always mounted for SEO. Images load only while open (shouldLoadImages={isOpen}).
// `inert` removes focusability the instant aria-expanded flips, so opacity and
// visibility are free to transition together without a focus/visual race.
const SubNavigation = ({ subnavItem, isOpen, onLinkClick, id }) => {
  return (
    <div
      id={id}
      className={`absolute left-0 w-full top-[50px] z-50 bg-warm-white border-b border-white transition-[opacity,visibility] duration-200 ${
        isOpen ? "opacity-100 visible" : "opacity-0 invisible"
      }`}
      inert={!isOpen}
    >
      <Container>
        <div
          className={`grid ${colsClass[subnavItem.columns.length]} gap-4 pt-8 pb-10 pr-4 pl-0`}
        >
          {subnavItem.columns.map((col, i) => (
            <div key={i} className="flex flex-col w-full gap-4 col-span-1">
              <ColumnRenderer
                layout={subnavItem.sub_nav_layout}
                col={col}
                onLinkClick={onLinkClick}
                shouldLoadImages={isOpen}
              />
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
};

// ─── Mobile drawer ────────────────────────────────────────────────────────────

const MobileDrawer = ({ open, navigation, onClose }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const { cart } = useCart();
  const itemCount = cart?.totalQuantity || 0;
  const drawerRef = useRef(null);

  // BL-816: dismissing the drawer keeps the open sub-nav panel, so reopening
  // lands where the user left off. Production does the same — measured on
  // birchliving.com across two close/reopen cycles, the panel stayed put.
  // Navigating is the one case that resets it: there every nav link is a full
  // page load, so nothing survives it, while here the drawer stays mounted
  // across a client-side route change and would otherwise reopen on the panel
  // belonging to the previous page.
  const handleDismiss = () => onClose();

  const handleNavigate = () => {
    setActiveIndex(null);
    onClose();
  };

  // Move focus into the drawer on open; it doesn't happen automatically.
  useEffect(() => {
    if (!open) return;
    drawerRef.current?.querySelector("a, button")?.focus();
  }, [open]);

  const activeItem = activeIndex !== null ? navigation[activeIndex] : null;

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={handleDismiss}
          aria-hidden="true"
        />
      )}
      <div
        ref={drawerRef}
        inert={!open}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        data-cy="mobile-drawer"
        onKeyDown={(e) => { if (e.key === "Escape") handleDismiss(); }}
        className={`fixed top-0 right-0 z-50 h-full w-full bg-warm-white flex flex-col transition-transform duration-300 overflow-hidden md:w-[26.6rem] ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Top bar: cart + close (both right-aligned) */}
        <div className="flex items-center justify-end gap-5 px-5 py-4 border-b border-gray-200/60">
          <Link
            href="/cart"
            aria-label="Cart"
            className="flex items-center justify-center text-primary min-w-6 min-h-6"
            onClick={handleNavigate}
          >
            <CartIcon count={itemCount} />
          </Link>
          <button
            onClick={handleDismiss}
            aria-label="Close menu"
            className="text-primary cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Sliding panels container */}
        <div className="relative flex-1 overflow-hidden">
          {/* Inert once slid offscreen: translate alone leaves links tabbable. */}
          {/* Both panels swing on activeItem, not on activeIndex: an index that
              no longer resolves would otherwise slide the main menu away and
              leave an empty panel in its place, with the back arrow gone since
              it renders inside activeItem. */}
          <div
            inert={!!activeItem}
            className={`absolute inset-0 flex flex-col transition-transform duration-300 ${
              activeItem ? "-translate-x-full" : "translate-x-0"
            }`}
          >
            <nav className="flex-1 overflow-y-auto">
              <ul className="mb-12 sm:mb-0">
                {/* BL-421: matches production's drawer, measured at 390px —
                    text starting 32px in, 18px labels, and 70px rows sitting
                    flush against each other with no divider between them. Ours
                    had 28px indent, 16px labels, 50px rows, and a border-b that
                    production does not draw. Note when re-measuring: production
                    keeps a 1px transparent border on the row, so compare border
                    *colour*, not width, and take font-size off the text node —
                    the row element there computes to 14px and an inner span
                    overrides it to 18px. */}
                {navigation.map((item, i) => (
                  <li
                    key={item.navigation_item_label + "_" + i}
                    className="px-4"
                  >
                    {item.sub_navigation.length ? (
                      <button
                        type="button"
                        onClick={() => setActiveIndex(i)}
                        data-cy={`drawer-navitem-${item.navigation_item_label.replace(/\s+/g, '-').toLowerCase()}`}
                        className="flex items-center justify-between w-full px-4 py-5 text-lg text-primary border border-transparent rounded-full hover:border-primary cursor-pointer"
                      >
                        <span>{item.navigation_item_label}</span>
                        <ChevronRightIcon />
                      </button>
                    ) : item?.link?.href ? (
                      <Link
                        prefetch={false}
                        href={item?.link?.href}
                        onClick={handleNavigate}
                        className="flex items-center justify-between w-full px-4 py-5 text-lg text-primary border border-transparent rounded-full hover:border-primary"
                      >
                        {item?.navigation_item_label}
                      </Link>
                    ) : null}
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Sub-nav panel */}
          <div
            className={`absolute inset-0 flex flex-col transition-transform duration-300 ${
              activeItem ? "translate-x-0" : "translate-x-full"
            }`}
          >
            {activeItem && (
              <>
                <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-200/60">
                  <button
                    type="button"
                    aria-label="Back to main menu"
                    onClick={() => setActiveIndex(null)}
                    className="flex items-center gap-2 text-primary cursor-pointer"
                  >
                    <BackArrowIcon />
                    <span className="text-base">{activeItem?.navigation_item_label}</span>
                  </button>
                </div>
                <nav className="flex-1 overflow-y-auto">
                  <div className="mb-12 sm:mb-0 flex flex-col gap-6 px-4 py-6">
                    {activeItem?.sub_navigation[0]?.columns.map((col, i) => (
                      <div key={i} className="flex flex-col gap-4">
                        <ColumnRenderer
                          layout={activeItem.sub_navigation[0].sub_nav_layout}
                          col={col}
                          onLinkClick={handleNavigate}
                          compact={true}
                          shouldLoadImages={activeIndex !== null}
                        />
                      </div>
                    ))}
                  </div>
                </nav>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Header ───────────────────────────────────────────────────────────────────

const HeaderClient = ({ headerData }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { brand = {}, navigation = [] } = headerData || {};
  const { cart } = useCart();
  const itemCount = cart?.totalQuantity || 0;
  const triggerRefs = useRef([]);
  const hamburgerRef = useRef(null);

  // Refocus the trigger on close so focus is never stranded on a hidden link.
  const closePanel = (i) => {
    setOpenIndex(null);
    triggerRefs.current[i]?.focus();
  };

  // onBlur bubbles, so one handler on the <li> covers trigger and panel.
  // Guarded on `current === i`: moving between triggers fires blur on the old
  // one after mouseenter already opened the new.
  const handlePanelBlur = (i) => (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setOpenIndex((current) => (current === i ? null : current));
    }
  };

  // Page state while the drawer is open: lock scroll, and trap focus by
  // marking <main>/#site-footer inert — they're siblings of <header>, not
  // children, so they can't be reached with props. #site-footer exists from
  // first paint, so content streaming into it inherits inert with no observer.
  useEffect(() => {
    document.documentElement.style.overflow = mobileOpen ? "hidden" : "";

    document.querySelectorAll("#main-content, #site-footer").forEach((el) => {
      el.inert = mobileOpen;
    });

    // Cleanup guards against an unmount while open e.g. global-error.js
    // replacing the root layout — leaving <html> permanently non-scrollable.
    return () => {
      document.documentElement.style.overflow = "";
      document.querySelectorAll("#main-content, #site-footer").forEach((el) => {
        el.inert = false;
      });
    };
  }, [mobileOpen]);
  // Close the drawer once the viewport reaches the xl breakpoint, where the
  // hamburger is hidden (xl:hidden) and the desktop nav takes over.
  useEffect(() => {
    const desktopMediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (e) => {
      if (e.matches) setMobileOpen(false);
    };
    handleChange(desktopMediaQuery);
    desktopMediaQuery.addEventListener("change", handleChange);
    return () => desktopMediaQuery.removeEventListener("change", handleChange);
  }, []);

  return (
    <>
      <header
        id="primaryNavHeader"
        className="sticky top-0 z-30 bg-warm-white"
        onMouseLeave={() => setOpenIndex(null)}
      >
        {/* BL-747: production's primary nav carries a 1px white bottom border,
            not a grey one — read off the live site, where the equivalent
            element computes to `rgb(255, 255, 255)`. HeaderSkeleton has the
            same class string and has to match, or the border flashes grey for
            the frame before this hydrates. */}
        <nav
          aria-label="Primary"
          className="relative border-b border-white h-[50px] flex items-center"
          data-nav="primary"
        >
          <Container className="flex items-center justify-between w-full h-full">
            {/* Left: Logo + Desktop nav */}
            <div className="flex items-center gap-x-4 h-full md:w-full">
              <Link
                href="/"
                title="Birch"
                aria-label={brand.brand_name || 'Birch'}
                className="z-10 focus-visible:outline-none focus-visible:ring-2 rounded-sm"
              >
                {brand.brand_name && (
                  <>
                    <BirchLogo />
                    <span className="sr-only">{brand.brand_name}</span>
                  </>
                )}
              </Link>

              <ul className="hidden xl:flex items-center h-full gap-x-0.5 mr-8">
                {navigation.map((item, i) => {
                  const hasSubNav = item.sub_navigation.length > 0;
                  const isOpen = openIndex === i;
                  const panelId = `header-subnav-${i}`;

                  // Not `relative`: the panel is `absolute left-0 w-full` and
                  // must keep resolving against the <nav> to span the
                  // viewport. A positioned <li> would shrink it to trigger width.
                  return (
                    <li
                      key={item.navigation_item_label + "_" + i}
                      className="flex items-center h-full group"
                      onMouseEnter={() => setOpenIndex(hasSubNav ? i : null)}
                      onBlur={handlePanelBlur(i)}
                      onKeyDown={(e) => { if (e.key === "Escape") closePanel(i); }}
                    >
                      {hasSubNav ? (
                        <>
                          <button
                            type="button"
                            ref={(el) => { triggerRefs.current[i] = el; }}
                            onClick={() => setOpenIndex(isOpen ? null : i)}
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            className="inline-flex items-center focus-visible:ring-2 focus-visible:ring-sea focus-visible:outline-none h-full lg:h-auto text-sm text-primary font-normal border border-transparent rounded-full hover:border-primary py-2 px-4 cursor-pointer"
                          >
                            <span className="whitespace-nowrap">
                              {item.navigation_item_label}
                            </span>
                            <ArrowIcon />
                          </button>
                          <SubNavigation
                            id={panelId}
                            subnavItem={item.sub_navigation[0]}
                            isOpen={isOpen}
                            onLinkClick={() => setOpenIndex(null)}
                          />
                        </>
                      ) : (
                        <Link
                          href={item.link.href}
                          className="inline-flex items-center text-sm text-primary font-normal border border-transparent rounded-full hover:border-primary py-2 px-4 focus-visible:ring-2 focus-visible:ring-sea focus-visible:outline-none"
                        >
                          <span className="whitespace-nowrap">
                            {item.navigation_item_label}
                          </span>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Cart + hamburger */}
            <div className="flex items-center gap-5 xl:gap-3">
              <Link
                href="/cart"
                aria-label="Cart"
                data-cy="cart-bag"
                // BL-467: the icon itself is 20x20, under the 24px minimum.
                className="flex items-center justify-center text-primary min-w-6 min-h-6"
              >
                <CartIcon count={itemCount} />
              </Link>
              <button
                ref={hamburgerRef}
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                data-cy="menu-open"
                className="xl:hidden text-primary"
              >
                <HamburgerIcon />
              </button>
            </div>
          </Container>
        </nav>
      </header>

      <MobileDrawer
        open={mobileOpen}
        navigation={navigation}
        onClose={() => {
          setMobileOpen(false);
          hamburgerRef.current?.focus();
        }}
      />
    </>
  );
};

export default HeaderClient;
